import React, { useMemo, useState } from 'react';
import { ArrowUpRight, ArrowDownRight, Clock, CheckCircle, Activity as ActivityIcon, CheckSquare, Square, Bot, FileText, Send, AlertTriangle, Check, X, FileSearch, ShieldAlert, Key, Server, Database, Cloud, Users, Zap } from 'lucide-react';
import { useCRM } from '../context/CRMContext';
import { useAuth, ROLES } from '../context/AuthContext';

// ============================================
// VISTA: ANALISTA (Viewer)
// ============================================
const GlobalDashboard = ({ policies, activities }) => {
  const metrics = useMemo(() => {
    let totalValue = 0;
    let newPoliciesThisMonth = 0;
    let pendingRenewals = [];
    
    policies.forEach(p => {
      if (p.stage === 'active') {
        const numValue = parseInt(p.value.replace(/[^0-9]/g, ''), 10);
        if (!isNaN(numValue)) totalValue += numValue;
      }
      if (p.stage !== 'lead' && p.stage !== 'renewal') newPoliciesThisMonth++;
      if (p.stage === 'renewal') pendingRenewals.push(p);
    });

    const conversionRate = policies.length > 0 ? Math.round((newPoliciesThisMonth / policies.length) * 100) : 0;

    return [
      { id: 1, title: 'Primas Activas (CLP)', value: new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(totalValue), trend: '+8%', isUp: true },
      { id: 2, title: 'Pólizas Nuevas', value: newPoliciesThisMonth.toString(), trend: '+2%', isUp: true },
      { id: 3, title: 'Tasa de Conversión', value: `${conversionRate}%`, trend: '0%', isUp: true },
      { id: 4, title: 'Renovaciones Pendientes', value: pendingRenewals.length.toString(), trend: 'Atención', isUp: false, renewals: pendingRenewals },
    ];
  }, [policies]);

  const recentActivities = activities.slice(0, 5);

  return (
    <div className="animate-fade-in">
      <h1>Panel de Control Analítico</h1>
      <p className="text-muted" style={{ marginBottom: '2rem' }}>Métricas corporativas e ingresos calculados en tiempo real (Visión de Lectura).</p>

      <div className="dashboard-metrics">
        {metrics.map((metric) => (
          <div key={metric.id} className="card metric-card">
            <div className="metric-header"><span>{metric.title}</span></div>
            <div className="metric-value">{metric.value}</div>
            <div className={`metric-trend ${metric.isUp ? 'trend-up' : 'trend-down'}`}>
              {metric.isUp ? <ArrowUpRight size={16} /> : <ArrowDownRight size={16} />}
              <span>{metric.trend}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="dashboard-grid" style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '1.5rem' }}>
        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          <h2 style={{ marginBottom: '1rem' }}>Muestra de Actividad</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', overflowY: 'auto' }}>
            {recentActivities.length === 0 ? (
              <p className="text-muted">No hay actividades recientes.</p>
            ) : (
              recentActivities.map(act => (
                <div key={act.id} style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <ActivityIcon size={20} color="var(--primary)" />
                  <div>
                    <p style={{ fontWeight: 500, fontSize: '0.9rem' }}>{act.text}</p>
                    <p className="text-muted" style={{ fontSize: '0.8rem' }}>{act.time}</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        
        <div className="card">
          <h2 style={{ marginBottom: '1rem' }}>Vencimientos</h2>
          {metrics[3].renewals.length === 0 ? (
            <p className="text-muted">No hay pólizas por vencer.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {metrics[3].renewals.map(r => (
                <div key={r.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem', backgroundColor: 'var(--danger-bg)', borderRadius: '4px' }}>
                  <div>
                    <p style={{ fontWeight: 600, fontSize: '0.85rem', color: 'var(--danger)' }}>{r.clientName}</p>
                    <p style={{ fontSize: '0.75rem', color: 'var(--danger)' }}>{r.type}</p>
                  </div>
                  <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--danger)' }}>{r.value}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ============================================
// VISTA: ADMINISTRADOR IT (COMMAND CENTER)
// ============================================
const AdminDashboard = ({ policies, activities }) => {
  const { policyTypes = [] } = useCRM();
  const adminMetrics = useMemo(() => {
    let totalValue = 0;
    policies.forEach(p => {
      if (p.stage === 'active') { totalValue += parseInt(p.value.replace(/[^0-9]/g, ''), 10) || 0; }
    });
    return new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP', maximumFractionDigits: 0 }).format(totalValue);
  }, [policies]);

  const totalAgregadas = policies.length;
  const totalValidadas = policies.filter(p => p.stage === 'active').length;
  const totalColaRiesgo = policies.filter(p => p.stage === 'underwriting').length;
  const totalErroresIA = Math.floor(totalAgregadas * 0.12) || 2; // Simulación estadística de errores IA para el Demo

  const chartData = useMemo(() => {
    const counts = {};
    policies.forEach(p => {
      const t = p.type || 'Otros';
      counts[t] = (counts[t] || 0) + 1;
    });
    
    const sorted = Object.keys(counts).map(key => ({
      name: key,
      count: counts[key]
    })).sort((a, b) => b.count - a.count);
    
    // Mostramos hasta 6 barras para no romper el flexbox
    const top6 = sorted.slice(0, 6);
    const max = top6.length > 0 ? top6[0].count : 1;
    return { data: top6, max };
  }, [policies]);

  return (
    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <div>
        <h1>Command Center (Administración)</h1>
        <p className="text-muted">Estado de integraciones, control de personal y métricas financieras.</p>
      </div>

      {/* METRICAS SUPERIORES - SISTEMA Y FINANZAS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.5rem' }}>
          <div style={{ padding: '1rem', backgroundColor: 'rgba(30, 64, 175, 0.1)', borderRadius: '12px' }}><Zap size={24} color="var(--primary)" /></div>
          <div>
            <p className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase' }}>Ingresos Globales</p>
            <h2 style={{ fontSize: '1.4rem' }}>{adminMetrics}</h2>
          </div>
        </div>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.5rem' }}>
          <div style={{ padding: '1rem', backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: '12px' }}><Users size={24} color="var(--success)" /></div>
          <div>
            <p className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase' }}>Personal Activo</p>
            <h2 style={{ fontSize: '1.4rem' }}>4 <span style={{fontSize: '0.9rem', color: 'var(--success)'}}>Online</span></h2>
          </div>
        </div>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.5rem' }}>
          <div style={{ padding: '1rem', backgroundColor: 'rgba(234, 179, 8, 0.1)', borderRadius: '12px' }}><ActivityIcon size={24} color="#eab308" /></div>
          <div>
            <p className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase' }}>Eventos Registrados</p>
            <h2 style={{ fontSize: '1.4rem' }}>{activities.length} <span style={{fontSize: '0.9rem', color: 'var(--text-muted)'}}>Hoy</span></h2>
          </div>
        </div>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.5rem' }}>
          <div style={{ padding: '1rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}><ShieldAlert size={24} color="#3b82f6" /></div>
          <div>
            <p className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase' }}>Nivel de Acceso</p>
            <h2 style={{ fontSize: '1.2rem', color: '#3b82f6' }}>Administrador Total</h2>
          </div>
        </div>
      </div>

      {/* METRICAS OPERATIVAS (POLIZAS Y CARGA) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '1.5rem' }}>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.5rem', backgroundColor: 'var(--surface)' }}>
          <div style={{ padding: '1rem', backgroundColor: 'rgba(59, 130, 246, 0.1)', borderRadius: '12px' }}><FileText size={20} color="#3b82f6" /></div>
          <div>
            <p className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase' }}>Ingresadas Total</p>
            <h2 style={{ fontSize: '1.4rem', color: 'var(--text-main)' }}>{totalAgregadas}</h2>
          </div>
        </div>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.5rem', backgroundColor: 'var(--surface)' }}>
          <div style={{ padding: '1rem', backgroundColor: 'rgba(34, 197, 94, 0.1)', borderRadius: '12px' }}><CheckCircle size={20} color="var(--success)" /></div>
          <div>
            <p className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase' }}>Revisadas / Activas</p>
            <h2 style={{ fontSize: '1.4rem', color: 'var(--success)' }}>{totalValidadas}</h2>
          </div>
        </div>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.5rem', backgroundColor: 'var(--surface)' }}>
          <div style={{ padding: '1rem', backgroundColor: 'rgba(234, 179, 8, 0.1)', borderRadius: '12px' }}><Clock size={20} color="#eab308" /></div>
          <div>
            <p className="text-muted" style={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase' }}>Cola Validación</p>
            <h2 style={{ fontSize: '1.4rem', color: '#eab308' }}>{totalColaRiesgo}</h2>
          </div>
        </div>
        <div className="card" style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '1.5rem', backgroundColor: 'var(--danger-bg)', border: '1px solid var(--danger)' }}>
          <div style={{ padding: '1rem', backgroundColor: 'white', border: '1px solid var(--danger)', borderRadius: '12px' }}><AlertTriangle size={20} color="var(--danger)" /></div>
          <div>
            <p style={{ fontSize: '0.85rem', fontWeight: 600, textTransform: 'uppercase', color: 'var(--danger)' }}>Errores IA Detenidos</p>
            <h2 style={{ fontSize: '1.4rem', color: 'var(--danger)' }}>{totalErroresIA} <span style={{fontSize: '0.8rem', color: 'var(--text-muted)'}}>Rechazos</span></h2>
          </div>
        </div>
      </div>

      {/* GRÁFICOS E INFRAESTRUCTURA */}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(350px, 1fr) 2fr', gap: '1.5rem' }}>
        
        {/* GRÁFICO NATIVO: DISTRIBUCIÓN DE CARTERA */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          <h2 style={{ marginBottom: '0.25rem' }}>Distribución de Cartera</h2>
          <p className="text-muted" style={{ fontSize: '0.85rem', marginBottom: '2.5rem' }}>Volumen por tipo de contrato</p>
          
          <div style={{ flex: 1, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', gap: '0.5rem', paddingBottom: '0.5rem', borderBottom: '1px solid var(--border)' }}>
            {chartData.data.map((item, idx) => (
              <div key={item.name} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', flex: 1 }}>
                <span style={{ fontSize: '0.75rem', marginBottom: '0.5rem', fontWeight: 600, color: 'var(--primary)' }}>{item.count}</span>
                <div 
                  className="bar-chart-node"
                  style={{ 
                    width: '100%', 
                    maxWidth: '36px', 
                    height: `${Math.max((item.count / chartData.max) * 140, 4)}px`, 
                    backgroundColor: policyTypes.find(t => t.name === item.name)?.color || 'var(--primary)', 
                    borderRadius: '4px 4px 0 0',
                    transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                    cursor: 'pointer'
                  }} 
                  title={`${item.count} pólizas de ${item.name}`}
                />
                <span style={{ marginTop: '0.5rem', fontSize: '0.65rem', fontWeight: 600, color: 'var(--text-muted)', textAlign: 'center', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {item.name.substring(0, 10)}
                </span>
              </div>
            ))}
            {chartData.data.length === 0 && (
              <p className="text-muted" style={{ alignSelf: 'center', padding: '2rem' }}>No hay datos suficientes para graficar.</p>
            )}
          </div>
        </div>

        {/* ZONA DE INFRAESTRUCTURA E INTEGRACIONES */}
        <div className="card" style={{ display: 'flex', flexDirection: 'column' }}>
          <h2 style={{ marginBottom: '1.5rem' }}>Salud de Integraciones Core</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', flex: 1 }}>
            
            <div style={{ padding: '1.5rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <Server size={32} color="var(--success)" style={{ marginBottom: '0.5rem' }} />
              <span style={{ fontWeight: 600 }}>Supabase (DB & Auth)</span>
              <span style={{ fontSize: '0.8rem', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '0.2rem', marginTop: '0.5rem' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'var(--success)' }}></div> Conectado y Sincronizado
              </span>
            </div>

            <div style={{ padding: '1.5rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <Cloud size={32} color="var(--success)" style={{ marginBottom: '0.5rem' }} />
              <span style={{ fontWeight: 600 }}>Alojamiento (Vercel Edge)</span>
              <span style={{ fontSize: '0.8rem', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '0.2rem', marginTop: '0.5rem' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'var(--success)' }}></div> app.tresquince.cl (Operativo)
              </span>
            </div>

            <div style={{ padding: '1.5rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <Database size={32} color="var(--success)" style={{ marginBottom: '0.5rem' }} />
              <span style={{ fontWeight: 600 }}>Data Hub (Ingesta)</span>
              <span style={{ fontSize: '0.8rem', color: 'var(--success)', display: 'flex', alignItems: 'center', gap: '0.2rem', marginTop: '0.5rem' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'var(--success)' }}></div> Parser CSV/Excel Activo
              </span>
            </div>

            <div style={{ padding: '1.5rem 1rem', border: '1px solid var(--border)', borderRadius: '8px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center' }}>
              <Bot size={32} color="var(--warning)" style={{ marginBottom: '0.5rem' }} />
              <span style={{ fontWeight: 600 }}>Motor Correos (Resend)</span>
              <span style={{ fontSize: '0.8rem', color: 'var(--warning)', display: 'flex', alignItems: 'center', gap: '0.2rem', marginTop: '0.5rem' }}>
                <div style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: 'var(--warning)' }}></div> Esperando Configuración
              </span>
            </div>

          </div>
        </div>
      </div>

      {/* FULL ACTIVITY LOG */}
      <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', maxHeight: '500px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem', borderBottom: '1px solid var(--border)', paddingBottom: '1rem' }}>
          <h2>Audit Log Global</h2>
          <span className="text-muted" style={{ fontSize: '0.85rem' }}>Historial en Tiempo Real de toda la organización</span>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', overflowY: 'auto', paddingRight: '1rem' }}>
          {activities.length === 0 ? (
            <p className="text-muted" style={{ textAlign: 'center', padding: '2rem' }}>El historial de auditoría está vacío.</p>
          ) : (
            activities.map(act => (
              <div key={act.id} style={{ display: 'flex', gap: '1.5rem', alignItems: 'flex-start', padding: '1rem', backgroundColor: 'var(--bg-color)', borderRadius: '6px' }}>
                <ActivityIcon size={20} color="var(--primary)" style={{ marginTop: '0.2rem' }} />
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: 500, fontSize: '0.95rem', color: 'var(--text-main)', marginBottom: '0.3rem' }}>{act.text}</p>
                  <p className="text-muted" style={{ fontSize: '0.8rem' }}>{act.time}</p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
      
    </div>
  );
};

// ============================================
// VISTA: EJECUTIVO COMERCIAL (WORKCENTER)
// ============================================
const ExecutiveDashboard = ({ policies, activities }) => {
  const clientsCount = policies.length; 
  const myActivePolicies = policies.filter(p => p.stage === 'active').length;
  const pendingRenewals = policies.filter(p => p.stage === 'renewal');
  const documentsSentCount = policies.filter(p => p.stage === 'quoted' || p.stage === 'underwriting').length;

  const [tasks, setTasks] = useState([
    { id: 1, title: 'Llamar a Juan Pérez por renovación Auto', done: false },
    { id: 2, title: 'Enviar PDF de Flota a la aseguradora', done: true },
    { id: 3, title: 'Subir contrato firmado de Carlos Ruiz', done: false }
  ]);

  const toggleTask = (id) => {
    setTasks(tasks.map(t => t.id === id ? { ...t, done: !t.done } : t));
  };

  return (
    <div className="animate-fade-in">
      <h1>Mi Zona de Trabajo</h1>
      <p className="text-muted" style={{ marginBottom: '2rem' }}>Priorización de tareas y seguimiento a clientes asignados.</p>

      {/* 4 KPIs de Ejecutivo */}
      <div className="dashboard-metrics" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
        <div className="card" style={{ padding: '1.5rem', textAlign: 'center', backgroundColor: 'var(--surface)' }}>
          <h3 className="text-muted" style={{ fontSize: '0.85rem', textTransform: 'uppercase' }}>Mis Clientes</h3>
          <p style={{ fontSize: '1.8rem', fontWeight: 700, marginTop: '0.5rem', color: 'var(--primary)' }}>{clientsCount}</p>
        </div>
        <div className="card" style={{ padding: '1.5rem', textAlign: 'center', backgroundColor: 'var(--surface)' }}>
          <h3 className="text-muted" style={{ fontSize: '0.85rem', textTransform: 'uppercase' }}>Pólizas Activas</h3>
          <p style={{ fontSize: '1.8rem', fontWeight: 700, marginTop: '0.5rem', color: 'var(--success)' }}>{myActivePolicies}</p>
        </div>
        <div className="card" style={{ padding: '1.5rem', textAlign: 'center', backgroundColor: 'var(--surface)' }}>
          <h3 className="text-muted" style={{ fontSize: '0.85rem', textTransform: 'uppercase' }}>Docs. Enviados</h3>
          <p style={{ fontSize: '1.8rem', fontWeight: 700, marginTop: '0.5rem', color: 'var(--info)' }}>{documentsSentCount}</p>
        </div>
        <div className="card" style={{ padding: '1.5rem', textAlign: 'center', backgroundColor: 'var(--danger-bg)', border: '1px solid var(--danger)' }}>
          <h3 style={{ fontSize: '0.85rem', textTransform: 'uppercase', color: 'var(--danger)' }}>Renovaciones Urgentes</h3>
          <p style={{ fontSize: '1.8rem', fontWeight: 700, marginTop: '0.5rem', color: 'var(--danger)' }}>{pendingRenewals.length}</p>
        </div>
      </div>

      <div className="dashboard-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
        
        {/* TAREAS PENDIENTES */}
        <div className="card">
          <h2 style={{ marginBottom: '1rem', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <CheckSquare size={18} color="var(--primary)" /> Tareas Pendientes (Follow-ups)
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {tasks.map(task => (
              <div 
                key={task.id} 
                onClick={() => toggleTask(task.id)}
                style={{ 
                  display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem', 
                  backgroundColor: task.done ? 'var(--bg-color)' : 'white', border: '1px solid var(--border)', 
                  borderRadius: '6px', cursor: 'pointer', opacity: task.done ? 0.6 : 1
                }}
              >
                {task.done ? <CheckSquare size={18} color="var(--success)" /> : <Square size={18} color="var(--text-muted)" />}
                <span style={{ textDecoration: task.done ? 'line-through' : 'none', fontSize: '0.9rem', color: 'var(--text-main)' }}>
                  {task.title}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ALERTAS IA INFORMATIVAS */}
        <div className="card" style={{ backgroundColor: '#fff7ed', border: '1px solid #ffedd5' }}>
          <h2 style={{ marginBottom: '1rem', fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#ea580c' }}>
            <Bot size={18} /> Asistente de IA (Informativo)
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ padding: '0.75rem', backgroundColor: 'white', borderRadius: '6px', borderLeft: '3px solid #f97316' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-main)', marginBottom: '0.25rem' }}>
                <strong>Sugerencia de VeritasVas:</strong> El cliente María González tiene un seguro de Vida, pero por su rango etario califica inmediatamente para una ampliación de Seguro de Salud (Cross-Selling).
              </p>
              <button style={{ background: 'none', border: 'none', color: 'var(--primary)', fontSize: '0.75rem', cursor: 'pointer', padding: 0, fontWeight: 600 }}>Generar Cotización Sugerida &rarr;</button>
            </div>
            <div style={{ padding: '0.75rem', backgroundColor: 'white', borderRadius: '6px', borderLeft: '3px solid #f97316' }}>
              <p style={{ fontSize: '0.85rem', color: 'var(--text-main)', marginBottom: '0.25rem' }}>
                <strong>Aviso Documental:</strong> Al escanear la póliza de Carlos Ruiz (Folio 4390), noto que adjuntó el contrato de la compañía anterior. Validador rechazará el expediente.
              </p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};


// ============================================
// VISTA: VALIDADOR DE RIESGO (RISK QUEUE)
// ============================================
const ValidatorDashboard = ({ policies, updatePolicyStage, logActivity }) => {
  const pendingCases = policies.filter(p => p.stage === 'underwriting');
  
  const [selectedCase, setSelectedCase] = useState(null);
  const [justification, setJustification] = useState('');
  const [showForceMode, setShowForceMode] = useState(false);

  const handleSelect = (c) => {
    setSelectedCase(c);
    setShowForceMode(false);
    setJustification('');
  };

  const handleApprove = () => {
    if(!selectedCase) return;
    updatePolicyStage(selectedCase.id, 'active');
    logActivity(`Validador Aprobó la póliza de ${selectedCase.clientName} (Monto: ${selectedCase.value}).`);
    setSelectedCase(null);
  };

  const handleReject = () => {
    if(!selectedCase) return;
    updatePolicyStage(selectedCase.id, 'lead');
    logActivity(`Validador Rechazó la póliza de ${selectedCase.clientName} y la devolvió al Ejecutivo.`);
    setSelectedCase(null);
  };

  const handleForceApprove = () => {
    if(justification.length < 10) return;
    updatePolicyStage(selectedCase.id, 'active');
    logActivity(`Validador Aprobación Forzada para ${selectedCase.clientName}. Motivo: "${justification}"`);
    setSelectedCase(null);
    setShowForceMode(false);
  };

  return (
    <div className="animate-fade-in" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <h1>Centro de Validación</h1>
        <p className="text-muted">Cola de expedientes sujetos a revisión humana y cruce documental AI.</p>
      </div>

      <div style={{ display: 'flex', gap: '1.5rem', flex: 1, height: 'calc(100vh - 200px)' }}>
        
        {/* PANEL IZQUIERDO: LA COLA (INBOX) */}
        <div className="card" style={{ width: '350px', padding: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <div style={{ padding: '1rem', backgroundColor: 'var(--secondary)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: 600 }}>Casos por Validar</span>
            <span style={{ backgroundColor: 'var(--danger)', color: 'white', padding: '0.2rem 0.6rem', borderRadius: '20px', fontSize: '0.8rem', fontWeight: 600 }}>{pendingCases.length}</span>
          </div>
          
          <div style={{ overflowY: 'auto', flex: 1 }}>
            {pendingCases.length === 0 ? (
              <p style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>La cola de validación está vacía. Excelente trabajo.</p>
            ) : (
              pendingCases.map(c => (
                <div 
                  key={c.id} 
                  onClick={() => handleSelect(c)}
                  style={{ 
                    padding: '1rem', borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'all 0.2s',
                    backgroundColor: selectedCase?.id === c.id ? 'rgba(30, 64, 175, 0.05)' : 'white',
                    borderLeft: selectedCase?.id === c.id ? '4px solid var(--primary)' : '4px solid transparent'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                    <span style={{ fontWeight: 600, color: 'var(--text-main)' }}>{c.clientName}</span>
                    <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>{c.type}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: '0.85rem', color: 'var(--primary)', fontWeight: 600 }}>{c.value}</span>
                    <span style={{ fontSize: '0.75rem', display: 'flex', alignItems: 'center', gap: '0.25rem', color: 'var(--danger)' }}><Bot size={12}/> Error Extracción IA</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* PANEL DERECHO: SPLIT VIEW (COMPARADOR) */}
        <div className="card" style={{ flex: 1, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {!selectedCase ? (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)' }}>
              <FileSearch size={48} style={{ marginBottom: '1rem', opacity: 0.5 }} />
              <h3>Selecciona un expediente</h3>
              <p>Revisa la cola pendiente a la izquierda para comenzar a validar.</p>
            </div>
          ) : (
            <div className="animate-fade-in" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              
              {/* Encabezado del Visor */}
              <div style={{ padding: '1.5rem', backgroundColor: 'var(--secondary)', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <h2 style={{ marginBottom: '0.25rem' }}>Expediente: {selectedCase.clientName}</h2>
                  <p className="text-muted" style={{ fontSize: '0.85rem' }}>ID: {selectedCase.id} | Modificado: Hoy</p>
                </div>
              </div>

              {/* Zona Mitad-Mitad (Mock Visor) */}
              <div style={{ display: 'flex', flex: 1 }}>
                
                {/* Visualizador Sintético del PDF Escaneado */}
                <div style={{ flex: 1, padding: '2rem', borderRight: '1px solid var(--border)', backgroundColor: '#f1f5f9', display: 'flex', flexDirection: 'column' }}>
                  <h4 style={{ color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.8rem', marginBottom: '1rem' }}>Original Subido (PDF)</h4>
                  <div style={{ flex: 1, backgroundColor: 'white', borderRadius: '4px', boxShadow: '0 4px 6px rgba(0,0,0,0.05)', padding: '2rem', fontFamily: 'monospace', fontSize: '0.85rem', color: '#475569', border: '1px solid #cbd5e1', position: 'relative' }}>
                    <div style={{ textAlign: 'center', borderBottom: '2px solid #cbd5e1', paddingBottom: '1rem', marginBottom: '2rem', fontWeight: 'bold' }}>CONTRATO ASEGURADORA S.A.</div>
                    <p>Contratante: {selectedCase.clientName}</p>
                    <p>Cobertura: {selectedCase.type}</p>
                    <br/>
                    <p>Las partes acuerdan que la prima anual asciende a la suma de:</p>
                    {/* Simulamos que la IA pilló que el PDF dice un valor menor que el ingresado */}
                    <p style={{ padding: '0.5rem', border: '2px dashed red', display: 'inline-block', backgroundColor: 'rgba(255,0,0,0.1)', fontWeight: 'bold', borderRadius: '4px', color: 'red' }}>VALOR: $250.000 / AÑO</p>
                    <br/><br/><br/><br/>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 'auto' }}>
                      <span style={{ borderTop: '1px solid #94a3b8', paddingTop: '0.5rem' }}>Firma Cliente</span>
                      <span style={{ borderTop: '1px solid #94a3b8', paddingTop: '0.5rem' }}>Firma Aseguradora</span>
                    </div>
                  </div>
                </div>

                {/* Datos del CRM y Errores IA */}
                <div style={{ flex: 1, padding: '2rem', display: 'flex', flexDirection: 'column', gap: '1.5rem', backgroundColor: 'white' }}>
                  <h4 style={{ color: 'var(--text-muted)', textTransform: 'uppercase', fontSize: '0.8rem' }}>Datos CRM vs Inteligencia Artificial</h4>
                  
                  <div style={{ padding: '1.5rem', backgroundColor: 'var(--bg-color)', borderRadius: '6px', border: '1px solid var(--border)' }}>
                    <p style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>Prima Registrada por el Ejecutivo:</p>
                    <p style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-main)' }}>{selectedCase.value}</p>
                  </div>

                  <div style={{ padding: '1.5rem', backgroundColor: 'var(--danger-bg)', borderRadius: '6px', border: '1px solid var(--danger)' }}>
                    <div style={{ display: 'flex', gap: '0.5rem', color: 'var(--danger)', marginBottom: '0.5rem', fontWeight: 600 }}>
                      <ShieldAlert size={20} /> ALERTA DE RECHAZO AUTOMÁTICO (IA)
                    </div>
                    <p style={{ fontSize: '0.9rem', color: 'var(--text-main)' }}>
                      Se ha detectado una divergencia crítica. El ejecutivo reportó un valor comercial de <strong>{selectedCase.value}</strong> para este prospecto, sin embargo, el análisis OCR del PDF original muestra un tope de $250.000 de contribución anual.
                    </p>
                  </div>

                  {/* Formularios de Acción o Justificación */}
                  <div style={{ marginTop: 'auto', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    
                    {showForceMode ? (
                      <div className="animate-fade-in" style={{ padding: '1rem', border: '2px solid var(--warning)', borderRadius: '6px', backgroundColor: '#fffbeb' }}>
                        <p style={{ fontSize: '0.85rem', fontWeight: 600, color: '#b45309', marginBottom: '0.5rem' }}><Key size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '0.2rem' }}/> Modo Forzar Aprobación Activado</p>
                        <textarea 
                          placeholder="Ingrese una justificación de mínimo 10 caracteres (ej: Aprobación validada vía correo gerencial folio #34)..."
                          value={justification}
                          onChange={e => setJustification(e.target.value)}
                          style={{ width: '100%', padding: '0.75rem', borderRadius: '4px', border: '1px solid #fcd34d', resize: 'none', height: '80px', marginBottom: '0.5rem' }}
                        />
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button className="btn" onClick={() => setShowForceMode(false)} style={{ flex: 1, backgroundColor: 'white' }}>Cancelar</button>
                          <button className="btn" onClick={handleForceApprove} disabled={justification.length < 10} style={{ flex: 1, backgroundColor: 'var(--warning)', color: 'white', border: 'none', opacity: justification.length < 10 ? 0.5 : 1 }}>Concluir Aprobación Forzosa</button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <button className="btn" onClick={handleApprove} style={{ width: '100%', justifyContent: 'center', backgroundColor: 'var(--success)', color: 'white', border: 'none', padding: '0.8rem' }}><Check size={18} /> Aprobar Expediente</button>
                        <button className="btn" onClick={handleReject} style={{ width: '100%', justifyContent: 'center', backgroundColor: 'var(--danger)', color: 'white', border: 'none', padding: '0.8rem' }}><X size={18} /> Rechazar Documento (Devolver)</button>
                        <div style={{ textAlign: 'center', marginTop: '0.5rem' }}>
                          <button onClick={() => setShowForceMode(true)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '0.8rem', textDecoration: 'underline', cursor: 'pointer' }}>Forzar aprobación a pesar del riesgo</button>
                        </div>
                      </>
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};


// ============================================
// ENRUTADOR PRINCIPAL POR ROL
// ============================================
const Dashboard = () => {
  const { policies, activities, updatePolicyStage, logActivity } = useCRM();
  const { user } = useAuth();
  
  if (user?.role === ROLES.ADMIN) {
    return <AdminDashboard policies={policies} activities={activities} />;
  }

  if (user?.role === ROLES.EDITOR) {
    return <ExecutiveDashboard policies={policies} activities={activities} />;
  }

  if (user?.role === ROLES.VALIDATOR) {
    return <ValidatorDashboard policies={policies} updatePolicyStage={updatePolicyStage} logActivity={logActivity} />;
  }

  // De forma predeterminada, Analistas (Viewers) caen al panel Analítico Global.
  return <GlobalDashboard policies={policies} activities={activities} />;
};

export default Dashboard;
